import React from 'react';
import { partition } from 'lodash';

import BoxBody from 'components/BoxBody';
import CenteredContainer from 'components/CenteredContainer';
import { ComposableTable, getTextCellGenerator } from 'components/ComposableTable';
import Container from 'components/Container';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import CoachingCoordinatorParticipantSidebar from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorParticipantSidebar';
import ErrorReporter from 'app/components/ErrorReporter';
import Heading from 'components/Heading';
import GetCoachingSessionsAndRecordingsContainer from 'coaching/containers/GetCoachingSessionsAndRecordingsContainer';
import GetCoachingSessionInstancesWithDistanceLearningMetadataContainer, {
  CoachingSessionInstanceWithDistanceLearningMetadata,
} from 'coaching/containers/GetCoachingSessionInstancesWithDistanceLearningMetadataContainer';
import LinkConstants from 'constants/LinkConstants';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';
import { ParticipantRow } from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorPeopleListView';
import { CoachingUserType } from 'globalTypes';

type Props = {
  cancellationReasons: string[];
};

type State = {
  selectedParticipant?: ParticipantRow;
};

export default class CoachingCoordinatorSessionsView extends React.Component<Props, State> {
  state: State = {};

  coachColumnHeaders() {
    return [{ name: 'Name', width: 150 }, { name: '' }, { name: 'Student name' }];
  }

  coachColumnGenerators(): Array<
    (data: CoachingSessionInstanceWithDistanceLearningMetadata) => React.ReactNode
  > {
    return [
      getTextCellGenerator(instance => instance.coach.name),
      getTextCellGenerator(instance => (
        <Link
          target="_blank"
          href={`${LinkConstants.R101_ADMIN_USER_BY_UUID_URL}${instance.coach.uuid}`}>
          View in R101
        </Link>
      )),
      getTextCellGenerator(instance => instance.participants.map(p => p.studentName)),
    ];
  }

  participantColumnHeaders() {
    return [{ name: 'Name', width: 150 }, { name: '' }];
  }

  participantColumnGenerators(): Array<
    (data: CoachingSessionInstanceWithDistanceLearningMetadata) => React.ReactNode
  > {
    return [
      getTextCellGenerator(instance => instance.participants.map(p => p.studentName)),
      getTextCellGenerator(instance =>
        instance.participants.map(p => (
          <Link
            target="_blank"
            key={p.uuid}
            href={`${LinkConstants.R101_ADMIN_USER_BY_UUID_URL}${p.studentUuid ||
              p.purchaserUuid}`}>
            View in R101
          </Link>
        ))
      ),
    ];
  }

  partitionInstancesByStatus(instances: CoachingSessionInstanceWithDistanceLearningMetadata[]) {
    const [lateCoachInstances, potentiallyLateParticipantInstances] = partition(
      instances,
      instance => instance.coachIsLate
    );
    const [lateParticipantInstances, allOtherInstances] = partition(
      potentiallyLateParticipantInstances,
      instance => instance.participantIsLate
    );
    return {
      lateCoachInstances,
      lateParticipantInstances,
      allOtherInstances,
    };
  }

  handleSelectParticipant = (selectedParticipant: ParticipantRow) => {
    this.setState({ selectedParticipant });
  };

  renderHeader(title: string, numberOfSessions: number) {
    return (
      <Row size="small">
        <MediaObject center>
          <Heading level="2" flush>
            <Text weight="semibold">{title}</Text>
          </Heading>
          <Text size="medium">{numberOfSessions}</Text>
        </MediaObject>
      </Row>
    );
  }

  render() {
    const { selectedParticipant } = this.state;
    const findMatchingRow = (instances: CoachingSessionInstanceWithDistanceLearningMetadata[]) => {
      const matchingRowKey = instances.find(i =>
        i.participants.some(p => p.uuid === selectedParticipant?.uuid)
      )?.coachingSessionUuid;
      return matchingRowKey ? [matchingRowKey] : [];
    };
    return (
      <GetCoachingSessionInstancesWithDistanceLearningMetadataContainer
        hoursToLookBack={1}
        hoursToLookAhead={1}>
        {({ instances, loading, error }) => {
          if (loading && !instances) {
            return <CenteredLoadingIndicator />;
          }
          if (error || !instances)
            return (
              <ErrorReporter error="CoachingCoordinatorSessionsView - could not fetch coaching sessions">
                <CenteredContainer size="medium">
                  <Text align="center" size="large" font="serif">
                    Oops! We couldn’t load the coaching sessions. Please refresh and try again. If
                    this issue persists, please notify an engineer.
                  </Text>
                </CenteredContainer>
              </ErrorReporter>
            );
          const { lateCoachInstances, lateParticipantInstances } = this.partitionInstancesByStatus(
            instances
          );
          return (
            <>
              <div className="CoachingCoordinatorView-content">
                <Container size="large">
                  <BoxBody>
                    <Row>
                      {this.renderHeader('Coach absent', lateCoachInstances.length)}
                      <ComposableTable<CoachingSessionInstanceWithDistanceLearningMetadata>
                        rows={lateCoachInstances}
                        columnHeaders={this.coachColumnHeaders()}
                        columnGenerators={this.coachColumnGenerators()}
                        fixedHeader
                        keyField="coachingSessionUuid"
                        keysOfActiveRows={findMatchingRow(lateCoachInstances)}
                        handleRowClick={row => this.handleSelectParticipant(row.participants[0])}
                      />
                    </Row>
                    <Row>
                      {this.renderHeader('Student absent', lateParticipantInstances.length)}
                      <ComposableTable<CoachingSessionInstanceWithDistanceLearningMetadata>
                        rows={lateParticipantInstances}
                        columnHeaders={this.participantColumnHeaders()}
                        columnGenerators={this.participantColumnGenerators()}
                        fixedHeader
                        keyField="coachingSessionUuid"
                        keysOfActiveRows={findMatchingRow(lateParticipantInstances)}
                        handleRowClick={row => this.handleSelectParticipant(row.participants[0])}
                      />
                    </Row>
                  </BoxBody>
                </Container>
              </div>
              {selectedParticipant && (
                <div className="CoachingCoordinatorView-sidebar">
                  <GetCoachingSessionsAndRecordingsContainer
                    participantUuid={selectedParticipant.uuid}
                    userType={CoachingUserType.participant}
                    distanceLearningSessionUuid={selectedParticipant.distanceLearningUuid}>
                    {({ upcomingSessions, previousSessions, sessionMedia, loading, error }) => {
                      return (
                        <CoachingCoordinatorParticipantSidebar
                          participant={selectedParticipant}
                          upcomingSessions={upcomingSessions}
                          previousSessions={previousSessions}
                          sessionMedia={sessionMedia}
                          sessionsLoading={loading}
                          sessionsError={error}
                          cancellationReasons={this.props.cancellationReasons}
                        />
                      );
                    }}
                  </GetCoachingSessionsAndRecordingsContainer>
                </div>
              )}
            </>
          );
        }}
      </GetCoachingSessionInstancesWithDistanceLearningMetadataContainer>
    );
  }
}
